import * as core from 'cw-ui-core';
import Routes from 'src/services/Routes';
import { clearUsersRoles, loadUsersRoles } from 'src/services/UserService';
import {
    applyPartnerRoleFilters,
    loadRoleFilterCatalogs,
    resolveSelectedRoleFilters,
} from 'src/services/UserRoleFilterService';
import Locale from 'src/Locale';
import {
    buildRoleFilterKey,
    filterUsersBySearch,
    getRoleFilterActive,
    getRoleFilterResults,
    isRoleFilterViewActive,
} from 'src/services/userRoleFilterUtils';

export { getRoleFilterActive, getRoleFilterResults, isRoleFilterViewActive };

export const USER_ROLE_FILTER_DIALOG_KEY = 'user_role_filter_dialog';

export const clearRoleFilterState = (dispatch, { clearCatalogs = false } = {}) => {
    dispatch(core.setScreenCustom(['roleFilterActive'], false));
    dispatch(core.setScreenCustom(['roleFilterApplied'], null));
    dispatch(core.setScreenCustom(['roleFilterIsApplying'], false));
    if (clearCatalogs) {
        dispatch(core.setScreenCustom(['roleFilterCatalogs'], null));
        dispatch(core.setScreenCustom(['roleFilterCatalogsPartnerId'], null));
    }
    dispatch(core.setScreenCustom(['roleFilterCatalogsLoading'], false));
    dispatch(core.removeModuleCache([Routes.USERS.id, 'roleFilterActive']));
    dispatch(core.removeModuleCache([Routes.USERS.id, 'roleFilterResults']));
};

export const toPlainArray = value => {
    if (!value) {
        return [];
    }
    if (typeof value.toJS === 'function') {
        return value.toJS();
    }
    return Array.isArray(value) ? value : [value];
};

export const getRoleFilterSearchTerm = state =>
    (core.getModuleCache(state.module, [Routes.USERS.id, 'search']) || '').trim();

export const paginateUsers = (users, page, pageSize) => {
    const totalPages = Math.max(1, Math.ceil(users.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const start = pageSize * (currentPage - 1);
    return {
        users: users.slice(start, start + pageSize),
        currentPage,
        totalPages,
    };
};

export const getDisplayUsersFromRoleFilter = state => {
    const roleFilterResults = getRoleFilterResults(state);
    if (!roleFilterResults) {
        return null;
    }

    const allUsers = toPlainArray(roleFilterResults);
    return filterUsersBySearch(allUsers, getRoleFilterSearchTerm(state));
};

export const syncRoleFilterPager = (displayUsers, page, pageSize) => {
    const { users, currentPage, totalPages } = paginateUsers(displayUsers, page, pageSize);
    core.Store().dispatch(core.setModuleCache([Routes.USERS.id, 'pager', 'totalPages'], totalPages));
    core.Store().dispatch(core.setModuleCache([Routes.USERS.id, 'pager', 'currentPage'], currentPage));
    return users;
};

export const loadRolesForUsersPage = users => {
    clearUsersRoles();
    if (users && users.length > 0) {
        loadUsersRoles(users);
    }
};

export const refreshRoleFilterView = (state, page, pageSize) => {
    const displayUsers = getDisplayUsersFromRoleFilter(state);
    if (!displayUsers) {
        return [];
    }

    const resolvedPage = page ?? (core.getModuleCache(state.module, [Routes.USERS.id, 'pager', 'currentPage']) || 1);
    const resolvedPageSize =
        pageSize ?? (core.getModuleCache(state.module, [Routes.USERS.id, 'pager', 'pageSize']) || 20);
    const usersPerPage = syncRoleFilterPager(displayUsers, resolvedPage, resolvedPageSize);
    loadRolesForUsersPage(usersPerPage);
    return usersPerPage;
};

export const resolvePartnerId = (partnerIdOverride = '') => {
    const state = core.Store().getState();
    return (
        partnerIdOverride ||
        core.getScreenCustom(state.screen, ['partnerId']) ||
        core.getModuleCache(state.module, [Routes.USERS.id, 'partnerId']) ||
        ''
    );
};

let roleFilterCatalogsLoadGeneration = 0;

export const loadRoleFilterCatalogsForPartner = async (dispatch, partnerId) => {
    if (!partnerId) {
        return;
    }

    const state = core.Store().getState();
    const cachedPartnerId = core.getScreenCustom(state.screen, ['roleFilterCatalogsPartnerId']);
    const cachedCatalogs = core.getScreenCustom(state.screen, ['roleFilterCatalogs']);

    if (cachedPartnerId === partnerId && cachedCatalogs) {
        return;
    }

    const loadGeneration = ++roleFilterCatalogsLoadGeneration;
    dispatch(core.setScreenCustom(['roleFilterCatalogsLoading'], true));
    try {
        const catalogs = await loadRoleFilterCatalogs(partnerId);
        if (loadGeneration !== roleFilterCatalogsLoadGeneration) {
            return;
        }
        dispatch(core.setScreenCustom(['roleFilterCatalogs'], catalogs));
        dispatch(core.setScreenCustom(['roleFilterCatalogsPartnerId'], partnerId));
    } catch (error) {
        if (loadGeneration !== roleFilterCatalogsLoadGeneration) {
            return;
        }
        const message = Locale.user_role_filter_catalog_load_error.defaultMessage;
        core.CwLog.error(`${message} ${error}`);
        dispatch(core.setErrorScreenMessage(message, false, null, USER_ROLE_FILTER_DIALOG_KEY));
    } finally {
        if (loadGeneration === roleFilterCatalogsLoadGeneration) {
            dispatch(core.setScreenCustom(['roleFilterCatalogsLoading'], false));
        }
    }
};

export const applyRoleFilters = async (dispatch, partnerId, selectedKeys) => {
    const resolvedPartnerId = resolvePartnerId(partnerId);
    const catalogs = core.getScreenCustom(core.Store().getState().screen, ['roleFilterCatalogs'])?.toJS() || {
        identityRoles: [],
    };
    const selectedFilters = resolveSelectedRoleFilters(selectedKeys, catalogs);

    if (!resolvedPartnerId || !selectedFilters.length) {
        return false;
    }

    dispatch(core.setScreenCustom(['roleFilterIsApplying'], true));

    const filteredUsers = await applyPartnerRoleFilters(resolvedPartnerId, selectedFilters, selectedKeys);

    if (filteredUsers === null) {
        dispatch(core.setScreenCustom(['roleFilterIsApplying'], false));
        return false;
    }

    const appliedFilters = selectedFilters.map(filter => ({
        ...filter,
        key: buildRoleFilterKey(filter.kind, filter.roleId),
    }));

    dispatch(core.setModuleCache([Routes.USERS.id, 'roleFilterActive'], true));
    dispatch(core.setModuleCache([Routes.USERS.id, 'roleFilterResults'], filteredUsers));
    dispatch(core.setScreenCustom(['roleFilterActive'], true));
    dispatch(core.setScreenCustom(['roleFilterApplied'], appliedFilters));

    const freshState = core.Store().getState();
    const pageSize = core.getModuleCache(freshState.module, [Routes.USERS.id, 'pager', 'pageSize']) || 20;
    const displayUsers = filterUsersBySearch(filteredUsers, getRoleFilterSearchTerm(freshState));
    const usersPerPage = syncRoleFilterPager(displayUsers, 1, pageSize);
    loadRolesForUsersPage(usersPerPage);

    dispatch(core.setScreenCustom(['roleFilterIsApplying'], false));

    return true;
};
